// Query keys and fetchers for the operator API. Read-only GETs only —
// mutations call `api()` directly from their components. The one-time
// launch code is deliberately NOT a query or a mutation result: it never
// enters the TanStack cache (see routes/_app/launcher.tsx).

import { api, qs } from "./api";
import type {
  Adjustment,
  AuditEvent,
  Attempt,
  Client,
  Environment,
  FaultRule,
  Identity,
  LaunchCode,
  Observation,
  ScenarioCatalogue,
  Transaction,
  TransactionDetail,
  User,
} from "./types";

export type Params = Record<string, string | undefined>;

export const keys = {
  me: () => ["mock", "me"] as const,
  publicEnvironment: () => ["mock", "environment-public"] as const,
  environment: () => ["mock", "environment"] as const,
  clients: () => ["mock", "clients"] as const,
  users: (p: Params) => ["mock", "users", p] as const,
  user: (id: string) => ["mock", "user", id] as const,
  adjustments: (id: string) => ["mock", "adjustments", id] as const,
  launchCodes: (userId: string) => ["mock", "launch-codes", userId] as const,
  transactions: (p: Params) => ["mock", "transactions", p] as const,
  transaction: (id: string) => ["mock", "transaction", id] as const,
  requests: (p: Params) => ["mock", "requests", p] as const,
  observations: (orderKey: string) => ["mock", "observations", orderKey] as const,
  faults: (activeOnly: boolean) => ["mock", "faults", activeOnly] as const,
  scenarios: () => ["mock", "scenarios"] as const,
  audit: (p: Params) => ["mock", "audit", p] as const,
};

const list = <T>(v: T[] | null | undefined): T[] => v ?? [];

export const fetchers = {
  /** Unauthenticated sandbox marking: the guard's pre-sign-in probe. */
  publicEnvironment: () => api<{ banner: string; environment_label: string }>("/environment/public"),
  environment: () => api<Environment>("/environment"),
  clients: async () => list(await api<Client[] | null>("/clients")),
  users: async (p: Params) => list(await api<User[] | null>(`/users${qs({ ...p, limit: 500 })}`)),
  user: (id: string) => api<User>(`/users/${encodeURIComponent(id)}`),
  adjustments: async (id: string) => list(await api<Adjustment[] | null>(`/users/${encodeURIComponent(id)}/adjustments`)),
  launchCodes: async (userId: string) => list(await api<LaunchCode[] | null>(`/launch-codes${qs({ user_id: userId, limit: 100 })}`)),
  transactions: async (p: Params) => list(await api<Transaction[] | null>(`/transactions${qs(p)}`)),
  transaction: (id: string) => api<TransactionDetail>(`/transactions/${encodeURIComponent(id)}`),
  requests: async (p: Params) => list(await api<Attempt[] | null>(`/requests${qs(p)}`)),
  observations: async (orderKey: string) => list(await api<Observation[] | null>(`/observations${qs({ order_key: orderKey, limit: 50 })}`)),
  faults: async (activeOnly: boolean) => list(await api<FaultRule[] | null>(`/faults${qs({ active: activeOnly ? "true" : undefined })}`)),
  scenarios: () => api<ScenarioCatalogue>("/scenarios"),
  audit: async (p: Params) => list(await api<AuditEvent[] | null>(`/audit${qs(p)}`)),
};

export async function fetchMeOrNull(): Promise<Identity | null> {
  try {
    return await api<Identity>("/auth/me");
  } catch (e) {
    const status = (e as { status?: number }).status;
    if (status === 401) return null;
    throw e;
  }
}

export const login = (username: string, password: string) =>
  api<Identity>("/auth/login", { method: "POST", body: { username, password } });

export const logout = () => api<void>("/auth/logout", { method: "POST" });
